<template>
  <v-dialog class="edit-user-details-popup"
            :value="visiblePopup"
            :max-width='380'
            @click:outside='onCloseClickButton'
            @keydown.esc="onCloseClickButton">
    <v-card class="edit-user-details-popup__wrapper" color="var(--bg-primary)">
      <div class="edit-user-details-popup__wrapper__title">{{$t("edit.user.popup.title")}}</div>
      <div class="edit-user-details-popup__wrapper__close">
        <span class='edit-user-details-popup__wrapper__close__close_btn' @click='onCloseClickButton'>
          <span class="material-icons-outlined">
            close
          </span>
        </span>
      </div>
      <div class="edit-user-details-popup__wrapper__form">
        <v-form ref="form" v-model="valid" @submit.prevent @keypress.enter.native="save">
          <div class='data-block'>
            <div class='data-title'>{{ $t('edit.user.popup.name') }}</div>
            <div class='text-container'>
              <v-text-field
                v-model="userData.userName"
                :rules="[rules.required]"
                outlined
                dense
                required
              >
              </v-text-field>
            </div>
          </div>
          <div class='data-block'>
            <div class='data-title'>{{ $t('edit.user.popup.email') }}</div>
            <div class='text-container'>
              <v-text-field
                v-model="userData.email"
                class="text-container__disabled"
                disabled
                outlined
                dense
              >
              </v-text-field>
            </div>
          </div>
          <div class='data-block'>
            <div class='data-title'>{{ $t('edit.user.popup.phone') }}</div>
            <div class='text-container'>
              <vue-tel-input-vuetify
                v-model="userData.phoneNumber"
                mode="international"
                :label="''"
                :rules="[rules.phone, rules.required]"
                outlined
                dense
                required
              />
            </div>
          </div>
        </v-form>
      </div>
      <div class='edit-user-details-popup__wrapper__error'>
        <span v-if='!valid'>
          <v-alert
            text
            dense
            color="#e0255e"
            icon="info"
            border="left"
            class='not-valid-message'
          >
          {{ $t('edit.user.not.valid.message') }}
        </v-alert>
      </span>
      </div>
      <div class='edit-user-details-popup__wrapper__footer'>
        <div class="buttons">
          <div>
            <v-btn class="btn-secondary cancel" @click="onCloseClickButton">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn class="btn-primary save" :disabled="!valid" @click="save">
              {{ $t('save') }}
            </v-btn>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import { CLEAR_USER_DETAILS_FOR_EDIT } from '@/store/user/actionTypes';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';
import userType from '@/constants/users';
import {rules} from '@/utils/validationUtils';

export default {
  name: 'EditUserDetailsPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  props: {
    visiblePopup: {
      type: Boolean,
      required: true,
    },
  },
  methods: {
    onCloseClickButton() {
      this.$emit('closed');
    },
    async save() {
      if (this.$refs.form.validate()) {
        this.$emit('save', this.userData);
      }
    },
    init() {
      this.userData = {};
      this.userData.id = this.userDetailsForEdit.id;
      this.userData.userName = this.userDetailsForEdit.userName;
      this.userData.email = this.userDetailsForEdit.email;
      this.userData.phoneNumber = this.userDetailsForEdit.phoneNumber;
      this.userData.access = this.userDetailsForEdit.access;
      this.userData.role = this.userDetailsForEdit.role;
    },
  },
  watch: {
    visiblePopup: {
      handler() {
        if (this.visiblePopup) {
          this.init();
        } else {
          this.$store.dispatch(CLEAR_USER_DETAILS_FOR_EDIT);
        }
      },
      immediate: true,
    },
  },
  data() {
    return {
      valid: false,
      userData: {},
    };
  },
  computed: {
    ...mapGetters(['userDetailsForEdit', 'companyData']),
    formattedType() {
      return (this.userData.email?.split('@')[1] === this?.companyData?.domain) ? userType.REGULAR : userType.EXTERNAL;
    },
    rules() {
      return {
        ...rules,
        required: v => rules.required(v) || this.$t('required'),
        phone: v => rules.phone(v) || this.$t('registration.client.info.phone.number.is.not.valid'),
      };
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.edit-user-details-popup {
  &__wrapper {
    max-width: rem(380);
    padding: $space-8 $space-10;

    &__title {
      color: var(--text-primary);
      font-family: $default-application-font-bold;
      font-size: $font-size-3xl;
      font-weight: bold;
      line-height: $font-size-3xl;
      letter-spacing: 0;
    }

    &__form {
      margin-bottom: $space-5;

      .data-block {
        .data-title {
          font-family: $default-application-font-regular;
          font-size: $font-size-xs;
          letter-spacing: 0.8px;
          line-height: $space-2;
          margin-top: $space-4;
          margin-bottom: $space-1;
          color: var(--text-secondary);
        }

        .text-container {
          &__disabled {
            background-color: var(--bg-tertiary);
          }
        }
      }
    }

    &__close {
      cursor: pointer;
      display: inline-block;
      position: absolute;
      right: $space-2;
      top: $space-4;
      font-size: $font-size-md;
      font-weight: 500;
      letter-spacing: 1.07px;
      line-height: $space-4;

      &__close_btn {
        margin-right: rem(5);
        margin-top: $space-2;
        float: right;

        &:hover {
          cursor: pointer;
          color: var(--brand-primary);
        }
      }
    }

    &__error {
      .not-valid-message {
        font-size: $font-size-xs;
        font-weight: 600;
        letter-spacing: 1.12px;
        line-height: $space-4;
      }
    }

    &__footer {
      display: flex;
      flex-direction: column;

      .buttons {
        display: flex;
        flex-direction: row;
        justify-content: space-between;

        .save {
          margin-left: $space-2;
        }

        .cancel {
          box-shadow: $shadow-sm;
        }
      }
    }
  }
}
</style>

<style lang="scss">
@import '~@/assets/styles/design-system';

.edit-user-details-popup__wrapper__form {
  .v-input--dense > .v-input__control > .v-input__slot.v-input__slot {
    margin-bottom: 0;
  }
}
</style>
